
import { FC, useState } from 'react'
import { Form, redirect, replace } from 'react-router-dom'

// COMPONENTS
import { Column } from '@components/templates/column'
import { BillingAddressFields } from '@components/forms'
import Button from '@components/atoms/button'

// TYPES
import type { Address } from '@stripe/stripe-js'
import type { RouteObject } from 'react-router-dom'

// UTILITIES
import { parseKycStatus, parseTeraUserData } from '@schemas'
import { createAction } from '@utils/actions'
import { isEmail, isNonEmptyString } from '@utils/type-checkers'
import { VirtualCardAPI } from '@apis'
import { createAuthenticatedLoader } from '@utils/loaders'


type OnAddressChangeParams = Parameters<NonNullable<React.ComponentProps<typeof BillingAddressFields>['onAddressChange']>>

const formId = 'billing-address'
const Component: FC<{}> = () => {
    const [billingAddress, setBillingAddress] = useState<{ address?: Address, valid?: boolean }>({})


    function handleSubmit() {
        console.log(billingAddress.address)
    }

    function handleAddressChange(...[address, valid]: OnAddressChangeParams) {
        console.log(address, valid)
        setBillingAddress({ address, valid })
    }

    return (<>
        <div className='flex flex-col' style={{ gap: '1rem', textAlign: 'center' }}>
            <h1>Receive Your Card!</h1>
            <p className='text-body'>Finish setting up your account by providing your billing address. <strong>This will be tied to your account’s virtual card!</strong></p>
        </div>
        <Form id={formId} method="POST" onSubmit={handleSubmit} className='flex flex-col' style={{ gap: '0.625rem', padding: '1rem', borderRadius: 'var(--border-radius-md)', border: '1px solid var(--splitcard-gray-200)' }}>
            <div className='flex flex-row items-center gap-4'>
                <div className="bubble-sml" style={{ backgroundImage: 'url(/icons/duotone/location.svg)' }} />
                <h4>Billing Address</h4>
            </div>
            <BillingAddressFields initAddress={billingAddress.address} onAddressChange={handleAddressChange} />
        </Form>
        <Column.Footer>
            <Button form={formId} type="submit" disabled={!billingAddress.valid}>Continue</Button>
        </Column.Footer>
    </>)
}

const loader = createAuthenticatedLoader(async (_, Clerk) => {
    const tera = parseTeraUserData(Clerk.user?.publicMetadata.tera)
    if (!!tera.address?.line1) return replace('/account')
    else return null
})

const action = createAction(async ({ request }) => {
    const formData = await request.formData()
    const line1 = formData.get('line1')
    const line2 = formData.get('line2') as string | null
    const city = formData.get('city')
    const state = formData.get('state')
    const postal_code = formData.get('postal_code')
    const country = formData.get('country')

    const user = window.Clerk?.user

    if (!isNonEmptyString(line1)
        || !isNonEmptyString(city)
        || !isNonEmptyString(state)
        || !(isNonEmptyString(postal_code) && postal_code.length === 5 && /^[0-9]*$/.test(postal_code))
        || !isNonEmptyString(country)
    ) throw new Error(`Invalid Billing Address: ${{ line1, line2, city, state, postal_code, country }}`);
    else if (!!user) {
        const address = {
            line1: line1,
            line2: line2,
            city: city,
            state: state,
            postal_code: postal_code,
            country: country,
        }

        const publicMetaData: UserPublicMetadata = {
            tera: parseTeraUserData({
                ...(user?.publicMetadata?.tera || {}),
                address: address
            }),
            kyc: parseKycStatus(user.publicMetadata?.kyc),
        }
        user.publicMetadata = publicMetaData

        await VirtualCardAPI.AddBillingAddressRequest.fetch(user.id, address)

        return redirect('../explainer')
    }
})


const AddCardBillingRoute: RouteObject = {
    path: "add-billing",
    Component,
    loader,
    action,
}

export { AddCardBillingRoute }
